type ContentProgressStatus = "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED";

interface OrderableContent {
    globalSortOrder: number;
}

/** Orders a course's content into course-wide step order via the server-computed `globalSortOrder`. */
export function orderByGlobalSortOrder<T extends OrderableContent>(content: T[]): T[] {
    return [...content].sort((a, b) => a.globalSortOrder - b.globalSortOrder);
}

export interface CourseStepProgress {
    status: ContentProgressStatus;
    currentStepIndex: number;
    totalSteps: number;
}

/** Derives course-level status and current step from per-content progress. Assumes `orderedContent` is already in step order (see `orderByGlobalSortOrder`). */
export function deriveCourseStepProgress(
    orderedContent: { id: string }[],
    progressByContentId: Map<string, ContentProgressStatus>,
): CourseStepProgress {
    const totalSteps = orderedContent.length;
    const firstIncompleteIndex = orderedContent.findIndex((c) => progressByContentId.get(c.id) !== "COMPLETED");
    const isComplete = firstIncompleteIndex === -1 && totalSteps > 0;
    const hasStarted = firstIncompleteIndex > 0 || isComplete;

    return {
        status: isComplete ? "COMPLETED" : hasStarted ? "IN_PROGRESS" : "NOT_STARTED",
        currentStepIndex: isComplete ? totalSteps - 1 : Math.max(firstIncompleteIndex, 0),
        totalSteps,
    };
}
